<template>
    <Modal 
        :isOpen="isOpen"
        :changeModalState="toggleFilterPopup"
        rounded="42px"
        :hideHeadersBorder="true"
    >
    <h5 class="text-center mt-2 mb-[20px] font-bold text-[18px]">{{ $t('search.are_you_sure_you_want_to_reset_the_filter') }}</h5>
    <form @submit.prevent="reset" class=" p-[20px] lg:px-[100px] " action="">
        <div class=" flex gap-2">
            <button @click="toggleFilterPopup(false)" class="text-primary border border-primary rounded-md text-center min-w-fit p-2 w-1/2 xl:w-1/3">{{
                $t('general.cancel') }}</button>
            <button @click="reset" class="text-white bg-primary rounded-md text-center min-w-fit p-2 w-1/2 xl:w-1/3">{{
                $t('general.reset') }}</button>
        </div>
    </form>
    </Modal>
        <div
            class="filter-container scrollable relative bg-[#F1F1F13B] rounded-[10px] border border-[#F1F1F18F] h-full w-full md:w-[350px] md:max-h-[calc(100vh-100px)] md:overflow-hidden">
    
            <div class="filter-content absolute left-0 top-0 h-fit w-full pb-[30px] md:h-full md:overflow-y-auto">
                <div class=" px-[20px] py-[25px] bg-[#e6e6e6] mb-8 flex items-center justify-between md:hidden">
                    <span class=" font-semibold">{{ $t('search.filter') }}</span>
                    <button @click="closeFilterPopup">
                        <img class="rotate-45" src="../../assets/icons/colordAdd.svg" alt="">
                    </button>
                </div>
    
                <div class="mt-[20px] mr-[20px] w-full">
                    <button class=" px-6 py-2 rounded-md bg-primary text-white font-semibold mx-auto hover:opacity-70"
                        @click="toggleFilterPopup(true)">{{ $t('search.reset') }}</button>
                </div>
    
                <div class="p-[20px]">
                    <Toggler @toggleTogglar="toggleTogglar" :val="filterObj.sort_by_force === 'verified'" name="verified"
                        :title="$t('search.certified_input')" />
                    
                    <FilterCategories 
                        :categories="allCategoriesData"
                        :selected-category-id="filterObj.category_id"
                        :search-all-categories="searchAllCategories"
                        @category-selected="chooseCategory"
                        @category-toggled="toggleCategroy"
                        @remove-category="removeSelectedCategory"
                    />
                    
                    <FilterSortDisplay
                        v-model="filterObj"
                        @sort-changed="handleSortChange"
                        @product-type-changed="handleProductTypeChange"
                        @rating-changed="handleRatingChange"
                    />
                    
                    <FilterLocation
                        :stores="allStores"
                        :governorates="governorates"
                        :all-governorates="allGovernorates"
                        :areas="areas"
                        :all-areas="allAreas"
                        :selected-store-id="filterObj.store_id"
                        :selected-governorate-id="filterObj.governorate_id"
                        :selected-area-id="filterObj.area_id"
                        :is-store-loading="isStoreLoading"
                        :allow-toget-stores="allowTogetStores"
                        @store-selected="changeSelectedStore"
                        @governorate-selected="changeSelectedGovernorate"
                        @area-selected="changeSelectedArea"
                        @fetch-stores="manipulateStoreData"
                        @remove-store="removeSelectedStore"
                        @remove-governorate="removeSelectedGovernorate"
                        @remove-area="removeSelectedArea"
                    />
                    
                    <FilterRanges
                        :price-state="price"
                        :spaces-state="spaces"
                        :show-spaces="getSelectedMainCategory() == 1"
                        :is-negotiable="filterObj.is_negotiable"
                        :min-price="filterObj.min_price"
                        :max-price="filterObj.max_price"
                        :min-space="filterObj.min_space"
                        :max-space="filterObj.max_space"
                        @price-submitted="validateAndFilterByPrice"
                        @spaces-submitted="validateAndFilterBySpaces"
                        @negotiable-changed="handleNegotiableChange"
                        @remove-price="removeSelectedPrice"
                        @remove-space="removeSelectedSpace"
                    />
                    
                    <FilterAttributes
                        v-if="withAttribute"
                        :attributes="allAttributes"
                        :selected-attribute-ids="filterObj.attribute_ids"
                        :year-range-limits="yearRangeLimits"
                        :year-range-values="yearRangeValues"
                        @attribute-toggled="toggleAttributeState"
                        @attribute-checked="handleSubAttributeCheck"
                        @nested-overlay-requested="openNestedOverlay"
                        @year-range-changed="onYearChange"
                        @attribute-removed="removeSelectedAttribute"
                    />
                    
                    <button
                        class=" bg-primary mx-auto mt-[20px] w-[200px] p-[16px] rounded-lg flex justify-center items-center md:hidden"
                        @click="closeFilterPopup">
                        <span class="text-white">{{ $t('general.confirm') }}</span>
                    </button>
                </div>
    
            </div>
        </div>
        
        <!-- Nested Attributes Overlay with Transition (Client-side only) -->
        <ClientOnly>
            <Transition name="overlay-slide">
                <NestedAttributeOverlay
                    v-if="activeNestedOverlay !== null"
                    :is-open="activeNestedOverlay !== null"
                    :nested-data="overlayData"
                    :position="overlayPosition"
                    :parent-info="overlayParentInfo"
                    @close="closeNestedOverlay"
                    @nested-check="handleNestedAttributeCheckFromOverlay"
                    @select-parent="handleSelectParentFromOverlay"
                />
            </Transition>
        </ClientOnly>
</template>

<script>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import Toggler from './Toggler.vue'
import { mapState } from 'pinia'
import { useGlobalStore } from "@/store/Modules/global.js"
import { MAINROLES, parseQueryString } from "@/helpers"
import Modal from '~/components/generic/Modal.vue'
import NestedAttributeOverlay from './NestedAttributeOverlay.vue'

// Import child components
import FilterCategories from './FilterCategories.vue'
import FilterSortDisplay from './FilterSortDisplay.vue'
import FilterLocation from './FilterLocation.vue'
import FilterRanges from './FilterRanges.vue'
import FilterAttributes from './FilterAttributes.vue'

// Import composables
import { useFilterState } from '@/composables/useFilterState'
import { useFilterManager } from '@/composables/useFilterManager'

export default {
    props: ['withAttribute', 'portalPage'],
    components: {
        Toggler,
        Modal,
        NestedAttributeOverlay,
        FilterCategories,
        FilterSortDisplay,
        FilterLocation,
        FilterRanges,
        FilterAttributes,
    },
    emits: ['filterStateChanged', 'closeFilter'],
    computed: {
        ...mapState(useGlobalStore, ['getFilterCategories']),
        decodeQueryObj(){
          const route = useRoute()
          try{
            let encodedQuery=route.fullPath.split('?')[1]
            let decodeQuery=decodeURIComponent(atob(encodedQuery))
            let queryObj=parseQueryString(decodeQuery)
            return queryObj
          }catch{
            return {}
          }
        },
    },

    setup(props) {
        const global = useGlobalStore()
        
        // Initialize filter state for UI (price, spaces validation)
        const {
            filterObj,
            price,
            spaces,
            resetFilters: resetFiltersLocal,
            validatePrice,
            validateSpaces,
            updateFilterObj,
            setPriceValues,
            setSpaceValues
        } = useFilterState()
        
        const userHasSelectedSorting = ref(false)
        
        // Initialize filter manager
        const filterManager = useFilterManager({
            portalPage: props.portalPage || 'products',
            enableUrlSync: true,
            debounceMs: 200,
        })
        
        // Subscribe to filter manager changes to sync filterObj and emit events
        filterManager.subscribeToChanges((state, source) => {
            // Sync manager state to filterObj for child components
            if (source !== 'filterbar') {
                Object.keys(state).forEach(key => {
                    if (key in filterObj && key !== 'filterQuery' && key !== 'attributesQuery') {
                        filterObj[key] = state[key]
                    }
                })
            }
            
            // Emit events for parent components (both new and legacy)
            // Note: This will be called from the component instance context via a method
        })
        
        return {
            global,
            filterObj,
            price,
            spaces,
            resetFilters: resetFiltersLocal,
            validatePrice,
            validateSpaces,
            updateFilterObj,
            setPriceValues,
            setSpaceValues,
            filterManager,
            userHasSelectedSorting,
        }
    },
    data() {
        return {
            isOpen: false,
            allCategoriesData: [],
            searchAllCategories: true,
            mainCategoryId: null,
            isInitializing: true,

            allGovernorates: [],
            governorates: [],
            allAreas: [],
            areas: [],
            allStores: [],
            isStoreLoading: false,
            allowTogetStores: true,
            currentStorePage: 1,
            allAttributes: [],
            filterTimeout: null,
            yearRangeLimits: {},
            yearRangeValues: {},
            yearChildrenMap: {},
            yearChangeTimeout: null,
            
            // Nested attributes overlay state
            activeNestedOverlay: null,
            overlayPosition: { x: 0, y: 0, maxHeight: 0 },
            overlayData: null,
            overlayParentInfo: null,
            overlayScrollHandler: null,
        }
    },
    methods: {
        toggleFilterPopup(val){
            this.isOpen = val
        },
        getSelectedMainCategory(){
            return this.allCategoriesData?.find((item)=>item.id == this.mainCategoryId)?.is_real_estate
        },
        // Calculate overlay position based on clicked element
        calculateOverlayPosition(event) {
            if (typeof window === 'undefined') {
                return { x: 0, y: 0, maxHeight: 400 }
            }
            
            const clickedElement = event.currentTarget
            const rect = clickedElement.getBoundingClientRect()
            const overlayWidth = 380
            const gap = 10
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight
            
            let x = rect.right + gap
            
            if (x + overlayWidth > viewportWidth) {
                x = rect.left - overlayWidth - gap
                
                if (x < 0) {
                    x = viewportWidth - overlayWidth - gap
                }
            }
            
            let y = rect.top
            let maxHeight = viewportHeight - y - 20
            
            if (maxHeight < 300) {
                y = Math.max(20, viewportHeight - 600)
                maxHeight = viewportHeight - y - 20
            }
            
            maxHeight = Math.max(maxHeight, 300)
            
            return {
                x: Math.max(0, x),
                y: Math.max(0, y),
                maxHeight: maxHeight
            }
        },
        // Open nested attributes overlay
        openNestedOverlay({ event, parentId, child }) {
            const overlayKey = `${parentId}-${child.id}`
            
            if (this.activeNestedOverlay === overlayKey) {
                this.closeNestedOverlay()
                return
            }
            
            this.overlayPosition = this.calculateOverlayPosition(event)
            this.overlayData = child.attributes || []
            this.overlayParentInfo = {
                parentId: parentId,
                childId: child.id,
                childNameAr: child.name_ar,
                childNameEn: child.name_en,
                isParentChecked: child.isChecked || false
            }
            this.activeNestedOverlay = overlayKey
            
            // Automatically select the "all option" (parent attribute) when overlay opens
            // Only select if parent is not already checked AND no nested attributes are selected
            this.$nextTick(() => {
                const parent = this.allAttributes.find(item => item.id === parentId)
                if (parent && parent.children) {
                    const currentChild = parent.children.find(childItem => childItem.id === child.id)
                    if (currentChild) {
                        const hasSelectedNested = this.hasNestedAttributesSelected(currentChild)
                        const isParentCurrentlySelected = currentChild.isChecked && !hasSelectedNested
                        
                        // Only auto-select if not already selected AND no nested attributes are selected
                        // This prevents resetting to "all option" when user has already selected specific nested options
                        if (!isParentCurrentlySelected && !hasSelectedNested) {
                            this.handleSelectParentFromOverlay({ parentId, childId: child.id })
                        }
                    }
                }
            })
        },
        // Close nested attributes overlay
        closeNestedOverlay() {
            this.activeNestedOverlay = null
            this.overlayData = null
            this.overlayParentInfo = null
        },
        // Handle nested attribute check from overlay
        handleNestedAttributeCheckFromOverlay(data) {
            const { parentId, childId, nestedAttrId, nestedChildId } = data
            this.handleNestedAttributeCheck(parentId, childId, nestedAttrId, nestedChildId)
            
            const parent = this.allAttributes.find(item => item.id === parentId)
            if (parent && parent.children) {
                const child = parent.children.find(childItem => childItem.id === childId)
                if (child && child.attributes) {
                    this.overlayData = [...child.attributes]
                    this.overlayParentInfo = {
                        ...this.overlayParentInfo,
                        isParentChecked: child.isChecked || false
                    }
                }
            }
        },
        // Handle "All" selection from overlay
        handleSelectParentFromOverlay(data) {
            const { parentId, childId } = data
            
            const parent = this.allAttributes.find(item => item.id === parentId)
            if (!parent || !parent.children) return
            
            const child = parent.children.find(childItem => childItem.id === childId)
            if (!child) return
            
            // Check if parent is currently selected and if nested attributes are selected
            const hasSelectedNested = this.hasNestedAttributesSelected(child)
            const isParentCurrentlySelected = child.isChecked
            
            // Toggle: if currently selected, unselect it; otherwise, select it
            if (isParentCurrentlySelected) {
                // Uncheck the parent
                child.isChecked = false
                // If nested attributes are selected, also clear them when unchecking parent
                if (hasSelectedNested && child.attributes && Array.isArray(child.attributes)) {
                    child.attributes = child.attributes.map((nestedAttr) => {
                        if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                            nestedAttr.children = nestedAttr.children.map((nestedChild) => {
                                nestedChild.isChecked = false
                                return nestedChild
                            })
                        }
                        return nestedAttr
                    })
                }
            } else {
                // Check the parent (child attribute) and uncheck all nested attributes
                // This ensures the checkbox in the main filter list is checked when "all option" is selected
                child.isChecked = true
                
                if (child.attributes && Array.isArray(child.attributes)) {
                    child.attributes = child.attributes.map((nestedAttr) => {
                        if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                            nestedAttr.children = nestedAttr.children.map((nestedChild) => {
                                nestedChild.isChecked = false
                                return nestedChild
                            })
                        }
                        return nestedAttr
                    })
                }
            }
            
            // Force reactivity update to ensure UI reflects the checked state
            // Create new object references to trigger Vue reactivity and ensure checkbox is properly updated
            const newCheckedState = !isParentCurrentlySelected
            this.allAttributes = this.allAttributes.map(parent => {
                if (parent.id === parentId && parent.children) {
                    return {
                        ...parent,
                        children: parent.children.map(c => {
                            if (c.id === childId) {
                                // Update with the new checked state to ensure checkbox reflects selection
                                return { ...c, isChecked: newCheckedState }
                            }
                            return c
                        })
                    }
                }
                return parent
            })
            this.$forceUpdate()
            this.updateSelectedAttributeIds()
            
            // Update overlay data and parent info to reflect the current state
            if (child.attributes) {
                this.overlayData = [...child.attributes]
            }
            // Always update overlay parent info to ensure checkbox state is synchronized
            this.overlayParentInfo = {
                ...this.overlayParentInfo,
                isParentChecked: child.isChecked || false
            }
        },
        // Setup scroll handler for closing overlay
        setupOverlayScrollHandler() {
            if (typeof document === 'undefined') return
            
            this.overlayScrollHandler = () => {
                if (this.activeNestedOverlay !== null) {
                    this.closeNestedOverlay()
                }
            }
            
            const filterContent = document.querySelector('.filter-content')
            if (filterContent) {
                filterContent.addEventListener('scroll', this.overlayScrollHandler)
            }
        },
        // Cleanup scroll handler
        cleanupOverlayScrollHandler() {
            if (typeof document === 'undefined') return
            
            if (this.overlayScrollHandler) {
                const filterContent = document.querySelector('.filter-content')
                if (filterContent) {
                    filterContent.removeEventListener('scroll', this.overlayScrollHandler)
                }
                this.overlayScrollHandler = null
            }
        },
        async getCategories() {
            try {
                const res = await this.makeRequest({
                    ...(this.isUserLoggedIn && { owner: MAINROLES.user }),
                    includeUID: true,
                    url: `${this.APIREQUESTS.user.getCategories.url}`,
                    method: this.APIREQUESTS.user.getCategories.method,
                })

                if (!res.status) {
                    this.notify({ type: "error", msg: res?.message || this.$t('general.failed') })
                } else {
                    const mainCategoryId = this.decodeQueryObj?.main_category_id
                    const categoryId = this.decodeQueryObj?.category_id
                    this.mainCategoryId = mainCategoryId
                    const categoriesData = res?.data || []
                    const handeledData = categoriesData?.map((item) => {
                        if(item.id == mainCategoryId){
                            item.isOpen = true
                            item.children = item.children.map((child)=>{
                                if(child.id == categoryId){
                                    child.isActive = true
                                    if (child.hasOwnProperty('has_price')) {
                                        this.filterObj.is_negotiable = !!child.has_price
                                    }
                                } else {
                                    child.isActive = false
                                }
                                return child
                            })
                            item.children.unshift({ 
                                id: item.id, 
                                name_ar: `كل ${item.name_ar}`, 
                                name_en: `All ${item.name_en}`, 
                                isActive: !categoryId && !!mainCategoryId 
                            })
                        } else {
                            item.isOpen = false
                            item.children = item.children.map((child)=>{
                                child.isActive = false
                                return child
                            })
                            item.children.unshift({ 
                                id: item.id, 
                                name_ar: `كل ${item.name_ar}`, 
                                name_en: `All ${item.name_en}`, 
                                isActive: false 
                            })
                        }
                        return item
                    })

                    this.allCategoriesData = handeledData
                }
            } catch (err) {
                console.error(err)
                this.notify({ type: "error", msg: err?.data?.message })
            }
        },

        reset() {
            // Close the modal first
            this.toggleFilterPopup(false)
            
            // Reset via filter manager FIRST (this resets all filter state)
            // Use skipNotification to prevent manager from emitting - we'll emit manually
            this.filterManager.resetFilters({ skipNotification: true })
            
            // Get reset state from manager
            const resetState = this.filterManager.getFilterState()
            
            // Sync manager reset state to filterObj for UI consistency
            Object.keys(resetState).forEach(key => {
                if (key in this.filterObj && key !== 'filterQuery' && key !== 'attributesQuery') {
                    this.filterObj[key] = resetState[key]
                }
            })
            
            // Reset local UI state
            this.resetFilters(this.yearRangeLimits, this.allAttributes)
            this.changeSelectedGovernorate(null)
            this.changeSelectedStore(null)
            this.userHasSelectedSorting = false
            
            // Reset category UI state
            if (this.$refs.allCategoriesCheckbox) {
                this.$refs.allCategoriesCheckbox.checked = true
            }
            this.filterObj.category_id = 0
            this.mainCategoryId = null
            this.searchAllCategories = true
            this.filterObj.is_negotiable = true
            this.allAttributes = []
            this.filterObj.attribute_ids = []
            
            // Reset year range values to full range
            Object.keys(this.yearRangeLimits).forEach(parentId => {
                const limits = this.yearRangeLimits[parentId]
                if (limits) {
                    this.yearRangeValues[parentId] = {
                        min: limits.min,
                        max: limits.max
                    }
                }
            })
            
            // Update category selection UI (without emitting events - already handled)
            const updatedCategories = this.allCategoriesData.map((main) => {
                if (Array.isArray(main.children) && main?.children?.length) {
                    main.children = main.children?.map((sub) => ({
                        ...sub,
                        isActive: false
                    }))
                }
                return main
            })
            this.allCategoriesData = updatedCategories
            
            // Emit events for parent components (single emission for reset)
            // Emit filterStateChanged event (new unified event)
            this.$emit('filterStateChanged', resetState, 'reset')
            
            // Emit handleQuery event (legacy - build query string from state)
            const queryString = this.filterManager.buildQueryString(resetState)
            this.$emit('handleQuery', queryString)
            
            // Emit handleAttributes for backward compatibility
            this.$emit('handleAttributes', "")
            
            // Emit handleCategoryId for backward compatibility
            if (this.$props.withAttribute) {
                this.$emit('handleCategoryId', 0)
            }
            
            // Force UI update to reflect reset state
            this.$nextTick(() => {
                this.$forceUpdate()
            })
        },
        
        validateAndFilterByPrice({ min, max }) {
            this.price.min_price.val = min
            this.price.max_price.val = max
            if (this.validatePrice()) {
                // Price is valid and has been set in filterObj
                this.updateFilterStateViaManager('validateAndFilterByPrice', {
                    min_price: this.filterObj.min_price,
                    max_price: this.filterObj.max_price,
                })
            }
        },
        
        validateAndFilterBySpaces({ min, max }) {
            this.spaces.min_space.val = min
            this.spaces.max_space.val = max
            if (this.validateSpaces()) {
                // Space is valid and has been set in filterObj
                this.updateFilterStateViaManager('validateAndFilterBySpaces', {
                    min_space: this.filterObj.min_space,
                    max_space: this.filterObj.max_space,
                })
            }
        },
        
        handleNegotiableChange(val) {
            this.filterObj.is_negotiable = val
            this.updateFilterStateViaManager('handleNegotiableChange', {
                is_negotiable: val,
            })
        },

        toggleAttributeState(id) {
            this.allAttributes = this.allAttributes.map((item) => {
                if (item?.id == id) {
                    item.isOpen = !item.isOpen
                }
                return item
            })
        },
        
        handleSubAttributeCheck({ parentId, childId }) {
            const parent = this.allAttributes.find(item => item.id === parentId)
            if (!parent || !parent.children) {
                return
            }
            
            const child = parent.children.find(childItem => childItem.id === childId)
            if (!child) {
                return
            }
            
            child.isChecked = !child.isChecked
            
            if (child.isChecked && child.attributes && child.attributes.length > 0) {
                child.isNestedOpen = true
            }
            
            if (child.isChecked && child.attributes && Array.isArray(child.attributes)) {
                child.attributes = child.attributes.map((nestedAttr) => {
                    if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                        nestedAttr.children = nestedAttr.children.map((nestedChild) => {
                            nestedChild.isChecked = false
                            return nestedChild
                        })
                    }
                    return nestedAttr
                })
            }
            
            if (!child.isChecked && child.attributes && Array.isArray(child.attributes)) {
                const hasSelectedNested = this.hasNestedAttributesSelected(child)
                if (hasSelectedNested) {
                    child.isNestedOpen = true
                }
            }
            
            this.allAttributes = [...this.allAttributes]
            this.$forceUpdate()
            this.updateSelectedAttributeIds()
        },
        
        handleNestedAttributeCheck(parentId, childId, nestedAttrId, nestedChildId) {
            const parent = this.allAttributes.find(item => item.id === parentId)
            if (!parent || !parent.children) {
                return
            }
            
            const child = parent.children.find(childItem => childItem.id === childId)
            if (!child || !child.attributes) {
                return
            }
            
            const nestedAttr = child.attributes.find(attr => attr.id === nestedAttrId)
            if (!nestedAttr || !nestedAttr.children) {
                return
            }
            
            const nestedChild = nestedAttr.children.find(nested => nested.id === nestedChildId)
            if (!nestedChild) {
                return
            }
            
            nestedChild.isChecked = !nestedChild.isChecked
            
            if (nestedChild.isChecked) {
                // Keep parent checked when nested attributes are selected
                child.isChecked = true
                
                child.attributes.forEach(attr => {
                    if (attr.children && Array.isArray(attr.children)) {
                        attr.children.forEach(nested => {
                            if (attr.id !== nestedAttrId && nested.isChecked) {
                                nested.isChecked = false
                            }
                        })
                    }
                })
                
                child.isNestedOpen = true
                nestedAttr.isNestedChildrenOpen = true
            } else {
                const hasSelectedNested = this.hasNestedAttributesSelected(child)
                if (!hasSelectedNested) {
                    child.isNestedOpen = false
                    nestedAttr.isNestedChildrenOpen = false
                    // If "all option" was selected (parent checked), keep it enabled
                    // Parent remains checked when all nested attributes are unchecked
                } else {
                    // If nested attributes are still selected, keep parent enabled
                    // This ensures parent stays checked when "all option" was selected
                    child.isChecked = true
                }
            }
            
            this.allAttributes = [...this.allAttributes]
            this.$forceUpdate()
            this.updateSelectedAttributeIds()
        },
        
        updateSelectedAttributeIds() {
            const selectedIds = []
            this.allAttributes.forEach(parent => {
                if (parent.children && Array.isArray(parent.children)) {
                    parent.children.forEach(child => {
                        let hasSelectedNestedAttributes = false
                        
                        if (child.attributes && Array.isArray(child.attributes)) {
                            child.attributes.forEach(nestedAttr => {
                                if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                                    nestedAttr.children.forEach(nestedChild => {
                                        if (nestedChild.isChecked) {
                                            selectedIds.push(nestedChild.id)
                                            hasSelectedNestedAttributes = true
                                        }
                                    })
                                }
                            })
                        }
                        
                        // Include parent ID if parent is checked
                        // When nested attributes are selected, parent remains checked and should be included
                        if (child.isChecked) {
                            selectedIds.push(child.id)
                        }
                    })
                }
            })
            
            const previousIds = [...(this.filterObj.attribute_ids || [])]
            const hasChanged = JSON.stringify(previousIds.sort()) !== JSON.stringify(selectedIds.sort())
            
            // Update the attribute_ids array - use spread to create new array reference for reactivity
            this.filterObj.attribute_ids = [...selectedIds]
            
            // Always update filter state when attributes change (even if IDs appear same, order might differ)
            // This ensures query is rebuilt and filters are properly removed
            if (hasChanged || previousIds.length !== selectedIds.length) {
                this.updateFilterStateViaManager('updateSelectedAttributeIds', {
                    attribute_ids: selectedIds,
                })
            }
        },
        
        /**
         * Helper method to update filter state via manager
         * Replaces triggerQueryBuild - manager handles URL sync and notifications
         */
        updateFilterStateViaManager(source = 'filterbar', updates = {}) {
            if (this.isInitializing) {
                return
            }
            
            // Sync current filterObj state to manager first (to ensure manager has all current values)
            const currentFilterState = {
                category_id: this.filterObj.category_id || null,
                main_category_id: this.mainCategoryId || null,
                attribute_ids: this.filterObj.attribute_ids || [],
                sort_by: this.filterObj.sort_by || '',
                sort_type: this.filterObj.sort_type || '',
                sort_by_force: this.filterObj.sort_by_force || '',
                userHasSelectedSorting: this.userHasSelectedSorting || false,
                product_type: this.filterObj.product_type || '',
                rating: this.filterObj.rating || '',
                min_price: this.filterObj.min_price || null,
                max_price: this.filterObj.max_price || null,
                min_space: this.filterObj.min_space || null,
                max_space: this.filterObj.max_space || null,
                governorate_id: this.filterObj.governorate_id || null,
                area_id: this.filterObj.area_id || null,
                store_id: this.filterObj.store_id || null,
                is_negotiable: this.filterObj.is_negotiable !== undefined ? this.filterObj.is_negotiable : true,
            }
            
            // Merge updates with current state
            const stateUpdates = { ...currentFilterState, ...updates }
            
            // Update via manager (handles URL sync and notifications)
            // Note: Manager will notify subscribers after debounce, but we also emit events immediately
            this.filterManager.updateFilterState(source, stateUpdates)
            
            // Emit events immediately for parent components
            // Get updated state (manager has already merged updates)
            const updatedState = this.filterManager.getFilterState()
            
            // Emit filterStateChanged event (new unified event)
            this.$emit('filterStateChanged', updatedState, source)
            
            // Emit handleQuery event (legacy - build query string from state)
            const queryString = this.filterManager.buildQueryString(updatedState)
            this.$emit('handleQuery', queryString)
        },
        
        restoreSelectedAttributes(attributeIds) {
            if (!Array.isArray(attributeIds) || attributeIds.length === 0) return
            
            const idsToRestore = attributeIds.map(id => String(id))
            
            this.allAttributes.forEach(parent => {
                if (parent.children && Array.isArray(parent.children)) {
                    parent.children.forEach(child => {
                        const childId = String(child.id)
                        if (idsToRestore.includes(childId)) {
                            child.isChecked = true
                            parent.isOpen = true
                            if (child.attributes && child.attributes.length > 0) {
                                child.isNestedOpen = true
                            }
                        }
                        
                        if (child.attributes && Array.isArray(child.attributes)) {
                            child.attributes.forEach(nestedAttr => {
                                if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                                    nestedAttr.children.forEach(nestedChild => {
                                        const nestedChildId = String(nestedChild.id)
                                        if (idsToRestore.includes(nestedChildId)) {
                                            nestedChild.isChecked = true
                                            parent.isOpen = true
                                            child.isNestedOpen = true
                                            nestedAttr.isNestedChildrenOpen = true
                                        }
                                    })
                                }
                            })
                        }
                    })
                }
            })
            
            this.updateSelectedAttributeIds()
            
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$forceUpdate()
                    this.allAttributes = [...this.allAttributes]
                }, 50)
            })
        },
        
        restoreUIStateFromFilterObj() {
            if (this.filterObj.min_price) {
                this.setPriceValues(this.filterObj.min_price, null)
            }
            if (this.filterObj.max_price) {
                this.setPriceValues(null, this.filterObj.max_price)
            }
            
            if (this.filterObj.min_space) {
                this.setSpaceValues(this.filterObj.min_space, null)
            }
            if (this.filterObj.max_space) {
                this.setSpaceValues(null, this.filterObj.max_space)
            }
            
            if (this.filterObj.store_id) {
                this.allStores = this.allStores.map(store => ({
                    ...store,
                    isSelected: store.id === this.filterObj.store_id || store.id == this.filterObj.store_id
                }))
            }
            
            if (this.filterObj.governorate_id) {
                this.allGovernorates = this.allGovernorates.map((item) => {
                    if (item.id == this.filterObj.governorate_id) {
                        item.isSelected = true
                        if (item.id !== "" && Array.isArray(item.areas)) {
                            const hasAllOption = item.areas.some(area => area.id === "")
                            if (!hasAllOption) {
                                item.areas.unshift({ id: "", name_ar: 'الكل', name_en: 'All', isSelected: true })
                            }
                            this.areas = item.areas.slice(0, 6)
                            this.allAreas = item.areas
                        }
                    } else {
                        item.isSelected = false
                    }
                    return item
                })
                
                this.governorates = this.allGovernorates.slice(0, 6)
                
                if (this.filterObj.area_id) {
                    this.$nextTick(() => {
                        this.allAreas = this.allAreas.map((area) => {
                            if (area.id == this.filterObj.area_id) {
                                area.isSelected = true
                            } else {
                                area.isSelected = false
                            }
                            return area
                        })
                        this.areas = this.allAreas.slice(0, 6)
                    })
                }
            }
        },
        
        changeSelectedStore(id) {
            this.allStores = this.allStores.map((item) => {
                if (item.id == id) {
                    this.filterObj.store_id = id
                    item.isSelected = true
                }
                else item.isSelected = false
                return item
            })
            
            if (id == null) {
                this.filterObj.store_id = null
            }
            
            // Update filter state after store change
            this.updateFilterStateViaManager('changeSelectedStore', {
                store_id: id || null,
            })
        },
        
        changeSelectedGovernorate(governorateId, options = {}) {
            this.allGovernorates = this.allGovernorates.map((item) => {
                if (item.id == governorateId) {
                    item.isSelected = true
                    this.filterObj.governorate_id = governorateId
                    this.filterObj.area_id = null

                    if (governorateId !== "" && Array.isArray(item.areas)) {
                        const hasAllOption = item.areas.some(area => area.id === "")
                        if (!hasAllOption) {
                            item.areas.unshift({ id: "", name_ar: 'الكل', name_en: 'All', isSelected: true })
                        }

                        this.areas = item.areas.slice(0, 6)
                        this.allAreas = item.areas
                    } else {
                        this.areas = []
                        this.allAreas = []
                    }

                }
                else item.isSelected = false
                return item
            })

            if (governorateId == null) {
                this.areas = []
                this.allAreas = []
                this.filterObj.governorate_id = null
                this.filterObj.area_id = null
            }
            
            if (!options.expand) {
                this.governorates = this.allGovernorates.slice(0, 6)
            }
            
            // Update filter state after governorate change
            this.updateFilterStateViaManager('changeSelectedGovernorate', {
                governorate_id: governorateId || null,
                area_id: governorateId ? this.filterObj.area_id : null, // Clear area if governorate cleared
            })
        },

        changeSelectedArea(areaId, options = {}) {
            this.allAreas = this.allAreas.map((item) => {
                if (item.id == areaId) {
                    item.isSelected = true
                    this.filterObj.area_id = areaId
                }
                else item.isSelected = false
                return item
            })
            
            if (areaId == null) {
                this.filterObj.area_id = null
            }
            
            if (!options.expand) {
                this.areas = this.allAreas.slice(0, 6)
            }
            
            // Update filter state after area change
            this.updateFilterStateViaManager('changeSelectedArea', {
                area_id: areaId || null,
            })
        },

        manipulateStoreData({ action, search }) {
            if (action == 'next') {
                this.currentStorePage += 1
            } else if (action == 'reset') {
                this.currentStorePage = 1
            } else if (action == 'search') {
                this.currentStorePage = 1
            }

            this.getStores(search || '')
        },

        toggleCategroy(id) {
            if (this.$props.withAttribute) {
                const categoryId = id == null ? 0 : id
                this.$emit('filterStateChanged', {
                    type: 'category',
                    categoryId: categoryId,
                })
            }

            const updatedCategories = this.allCategoriesData.map((item) => {
                if (item.id == id) {
                    item.isOpen = !item.isOpen
                }
                return item
            })

            this.allCategoriesData = updatedCategories
        },
        
        chooseCategory(id) {
            // Clear attributes when category changes
            this.updateFilterStateViaManager('chooseCategory', {
                attribute_ids: [],
            })
            
            // Emit for backward compatibility
            this.$emit('handleAttributes', "")

            if (id) {
                this.searchAllCategories = false
                
                let isMainCategory = false
                let selectedSubCategory = null
                let mainCategory = null
                
                this.allCategoriesData.forEach(main => {
                    if (main.id === id) {
                        isMainCategory = true
                        mainCategory = main
                        this.mainCategoryId = main.id
                    }
                    
                    if (Array.isArray(main.children) && main.children.length) {
                        const subCategory = main.children.find(sub => sub.id === id)
                        if (subCategory) {
                            selectedSubCategory = subCategory
                        }
                    }
                })
                
                if (isMainCategory) {
                    if (mainCategory && mainCategory.children && mainCategory.children.length > 1) {
                        const firstSubCategory = mainCategory.children.find(child => child.id !== mainCategory.id)
                        this.filterObj.category_id = firstSubCategory?.id || id
                    } else {
                        this.filterObj.category_id = id
                    }
                } else {
                    this.filterObj.category_id = id
                }
                
                if (selectedSubCategory && selectedSubCategory.hasOwnProperty('has_price')) {
                    this.filterObj.is_negotiable = !!selectedSubCategory.has_price
                }

                this.getAttributes(this.filterObj.category_id)
            } else {
                if (this.$refs.allCategoriesCheckbox) {
                    this.$refs.allCategoriesCheckbox.checked = true
                }
                this.filterObj.category_id = 0
                this.mainCategoryId = null
                this.searchAllCategories = true
                this.filterObj.is_negotiable = true
                this.allAttributes = []
                this.filterObj.attribute_ids = []
            }

            // Emit for backward compatibility
            if (this.$props.withAttribute) {
                this.$emit('handleCategoryId', this.filterObj.category_id)
                this.$emit('filterStateChanged', {
                    type: 'category',
                    categoryId: this.filterObj.category_id,
                })
            }
            
            const updatedCategories = this.allCategoriesData.map((main) => {
                if (Array.isArray(main.children) && main?.children?.length) {
                    main.children = main.children?.map((sub) => ({
                        ...sub,
                        isActive: sub.id === id
                    }))
                }
                return main
            })

            this.allCategoriesData = updatedCategories
            
            // Update filter state after category change
            const categoryUpdates = {
                category_id: this.filterObj.category_id,
                is_negotiable: this.filterObj.is_negotiable,
            }
            
            // Include main/sub category IDs if available
            if (this.mainCategoryId) {
                categoryUpdates.main_category_id = this.mainCategoryId
            }
            
            this.updateFilterStateViaManager('chooseCategory', categoryUpdates)
        },

        toggleTogglar(inputName, val) {
            if (inputName == 'agreedProducts') {
                this.filterObj.is_negotiable = val
            } else if (inputName == 'verified') {
                this.filterObj.sort_by_force = val ? 'verified' : ''
            } else {
                if (val) {
                    this.filterObj.sort_by = inputName
                    if (inputName === 'views' || inputName === 'sales' || inputName === 'discount') {
                        this.userHasSelectedSorting = true
                    } else {
                        this.filterObj.sort_type = null
                    }
                }
                else {
                    this.filterObj.sort_by = ""
                    if (!this.views && !this.discount && !this.filterObj.sort_by) {
                        this.userHasSelectedSorting = false
                    }
                    this.filterObj.sort_type = null
                }
            }
            
            // Update filter state after toggle change
            const updates = {
                sort_by: this.filterObj.sort_by,
                sort_type: this.filterObj.sort_type,
                sort_by_force: this.filterObj.sort_by_force,
                userHasSelectedSorting: this.userHasSelectedSorting,
            }
            
            this.updateFilterStateViaManager('toggleTogglar', updates)
        },

        closeFilterPopup() {
            this.$emit('closeFilter')
        },

        async getGovernorates() {
            try {
                const res = await this.makeRequest({
                    ...(this.isUserLoggedIn && { owner: MAINROLES.user }),
                    includeUID: true,
                    url: `${this.APIREQUESTS.user.getGovernorates.url}?with_areas=1`,
                    method: this.APIREQUESTS.user.getGovernorates.method,
                })

                if (!res.status) return

                if (Array.isArray(res?.data)) {
                    let allGov = res?.data
                    allGov.unshift({ id: "", name_ar: 'الكل', name_en: 'All' })

                    allGov = allGov.map((item) => {
                        if (item.id == "") item.isSelected = true
                        else item.isSelected = false

                        return item
                    })

                    this.governorates = allGov.slice(0, 6)
                    this.allGovernorates = allGov
                } else {
                    this.allGovernorates = []
                }
            } catch (err) {
                console.error(err)
            }
        },

        async getAttributes(id) {
            if (!id) return

            try {
                const res = await this.makeRequest({
                    ...(this.isUserLoggedIn && { owner: MAINROLES.user }),
                    includeUID: true,
                    url: `${this.APIREQUESTS.user.getAttributes.url}?category_id=${id}`,
                    method: this.APIREQUESTS.user.getAttributes.method,
                })

                if (!res.status) return

                if (Array.isArray(res?.data)) {
                    let allAttr = res?.data.map((main) => ({
                        ...main,
                        isOpen: false,
                        show_in_filter: main.show_in_filter,
                        children: main.children?.map((child) => ({
                            ...child,
                            isChecked: false,
                            isNestedOpen: false,
                            attributes: child.attributes?.map((nestedAttr) => ({
                                ...nestedAttr,
                                isNestedChildrenOpen: false,
                                children: nestedAttr.children?.map((nestedChild) => ({
                                    ...nestedChild,
                                    isChecked: false
                                }))
                            }))
                        }))
                    }))

                    const savedAttributeIds = [...(this.filterObj.attribute_ids || [])]
                    
                    this.allAttributes = allAttr
                    this.filterObj.attribute_ids = []
                    
                    // Parse year data for attributes with is_year flag
                    allAttr.forEach((main) => {
                        if (main.is_year && main.children && Array.isArray(main.children) && main.children.length > 0) {
                            const yearData = this.parseYearDataFromChildren(main.id, main.children)
                            this.yearRangeLimits[main.id] = yearData.limits
                            this.yearChildrenMap[main.id] = yearData.childrenMap
                            this.yearRangeValues[main.id] = {
                                min: yearData.limits.min,
                                max: yearData.limits.max
                            }
                        }
                    })
                    
                    if (savedAttributeIds.length > 0) {
                        this.$nextTick(() => {
                            setTimeout(() => {
                                this.restoreSelectedAttributes(savedAttributeIds)
                            }, 100)
                        })
                    }
                } else {
                    this.allAttributes = []
                    this.filterObj.attribute_ids = []
                }
            } catch (err) {
                console.error('Error fetching attributes:', err)
            }
        },

        async getStores(searchInput = '') {
            this.isStoreLoading = true

            try {
                const res = await this.makeRequest({
                    ...(this.isUserLoggedIn && { owner: MAINROLES.user }),
                    includeUID: true,
                    url: `${this.APIREQUESTS.user.getStores.url}?short=1&per_page=6&page=${this.currentStorePage}&search=${searchInput}`,
                    method: this.APIREQUESTS.user.getStores.method,
                })

                if (!res.status) {
                    this.notify({ type: "error", msg: res?.message || this.$t('general.failed') })
                    return
                }

                const stores = res?.data || []
                if (stores.length < 6) {
                    this.allowTogetStores = false
                } else {
                    this.allowTogetStores = true
                }

                const currentStores = stores.map((item) => {
                    item.isSelected = false
                    return item
                })

                if (this.currentStorePage == 1 && searchInput.length == 0) {
                    currentStores.unshift({ id: "", name: this.$t('search.all'), isSelected: true, noImg: true })
                }

                if (this.currentStorePage != 1) {
                    this.allStores.push(...currentStores)
                } else {
                    this.allStores = currentStores
                }
            } catch (err) {
                console.error(err)
            } finally {
                this.isStoreLoading = false
            }
        },
        
        parseYearDataFromChildren(parentId, children) {
            let minYear = Infinity
            let maxYear = -Infinity
            const childrenMap = {}
            
            const nameKey = this.$i18n.locale === 'ar' ? 'name_ar' : 'name_en'
            
            children.forEach(child => {
                const childName = child[nameKey] || child.name_ar || child.name_en || ''
                let childMin, childMax
                
                if (childName.includes(':')) {
                    const parts = childName.split(':').map(part => parseInt(part.trim()))
                    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                        childMin = Math.min(parts[0], parts[1])
                        childMax = Math.max(parts[0], parts[1])
                    }
                } else {
                    const year = parseInt(childName.trim())
                    if (!isNaN(year)) {
                        childMin = year
                        childMax = year
                    }
                }
                
                if (childMin !== undefined && childMax !== undefined) {
                    minYear = Math.min(minYear, childMin)
                    maxYear = Math.max(maxYear, childMax)
                    
                    for (let year = childMin; year <= childMax; year++) {
                        if (!childrenMap[year]) {
                            childrenMap[year] = []
                        }
                        childrenMap[year].push(child)
                    }
                }
            })
            
            return {
                limits: {
                    min: minYear === Infinity ? 2010 : minYear,
                    max: maxYear === -Infinity ? new Date().getFullYear() : maxYear
                },
                childrenMap
            }
        },
        
        onYearChange({ parentId, range }) {
            this.yearRangeValues[parentId] = {
                min: range.min,
                max: range.max
            }
            
            const parent = this.allAttributes.find(attr => attr.id === parentId)
            if (!parent || !parent.children) return
            
            const foundChildIds = new Set()
            const nameKey = this.$i18n.locale === 'ar' ? 'name_ar' : 'name_en'
            
            parent.children.forEach(child => {
                if (!child) return
                
                const childName = child[nameKey] || child.name_ar || child.name_en || ''
                let childMin, childMax
                
                if (childName.includes(':')) {
                    const parts = childName.split(':').map(part => parseInt(part.trim()))
                    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
                        childMin = Math.min(parts[0], parts[1])
                        childMax = Math.max(parts[0], parts[1])
                    }
                } else {
                    const year = parseInt(childName.trim())
                    if (!isNaN(year)) {
                        childMin = year
                        childMax = year
                    }
                }
                
                if (childMin !== undefined && childMax !== undefined) {
                    if (range.min <= childMax && range.max >= childMin) {
                        foundChildIds.add(child.id)
                    }
                }
            })
            
            parent.children.forEach(child => {
                child.isChecked = foundChildIds.has(child.id)
            })
            
            this.allAttributes = [...this.allAttributes]
            this.$forceUpdate()
            
            if (this.yearChangeTimeout) {
                clearTimeout(this.yearChangeTimeout)
            }
            
            this.yearChangeTimeout = setTimeout(() => {
                this.updateSelectedAttributeIds()
            }, 300)
        },
        
        handleSortChange({ sort_by, sort_type }) {
            this.userHasSelectedSorting = true
            this.filterObj.sort_by = sort_by
            this.filterObj.sort_type = sort_type
            this.updateFilterStateViaManager('handleSortChange', {
                sort_by,
                sort_type,
                userHasSelectedSorting: true,
            })
        },
        
        handleProductTypeChange(productType) {
            // Explicitly update filterObj.product_type to ensure it's cleared when empty
            this.filterObj.product_type = productType || ''
            // Update filter state to update URL and query string
            this.updateFilterStateViaManager('handleProductTypeChange', {
                product_type: productType || '',
            })
        },
        
        handleRatingChange(rating) {
            // Explicitly update filterObj.rating to ensure it's cleared when empty
            this.filterObj.rating = rating || ''
            // Update filter state to update URL and query string
            this.updateFilterStateViaManager('handleRatingChange', {
                rating: rating || '',
            })
        },
        
        removeSelectedAttribute({ parentId, attrId, type }) {
            if (type === 'year') {
                const parent = this.allAttributes.find(attr => attr.id === parentId)
                if (parent && parent.children) {
                    parent.children.forEach(child => {
                        child.isChecked = false
                    })
                }
                if (this.yearRangeLimits[parentId]) {
                    this.yearRangeValues[parentId] = {
                        min: this.yearRangeLimits[parentId].min,
                        max: this.yearRangeLimits[parentId].max
                    }
                }
            } else if (type === 'child') {
                this.allAttributes = this.allAttributes.map(parent => {
                    if (parent.id === parentId && parent.children) {
                        parent.children = parent.children.map(child => {
                            if (child.id === attrId) {
                                child.isChecked = false
                                // Also clear nested attributes when removing child
                                if (child.attributes && Array.isArray(child.attributes)) {
                                    child.attributes = child.attributes.map(nestedAttr => {
                                        if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                                            nestedAttr.children = nestedAttr.children.map(nestedChild => {
                                                nestedChild.isChecked = false
                                                return nestedChild
                                            })
                                        }
                                        return nestedAttr
                                    })
                                }
                            }
                            return child
                        })
                    }
                    return parent
                })
            } else if (type === 'nested') {
                this.allAttributes = this.allAttributes.map(parent => {
                    if (parent.id === parentId && parent.children) {
                        parent.children = parent.children.map(child => {
                            if (child.attributes && Array.isArray(child.attributes)) {
                                child.attributes = child.attributes.map(nestedAttr => {
                                    if (nestedAttr.children && Array.isArray(nestedAttr.children)) {
                                        nestedAttr.children = nestedAttr.children.map(nestedChild => {
                                            if (nestedChild.id === attrId) {
                                                nestedChild.isChecked = false
                                            }
                                            return nestedChild
                                        })
                                    }
                                    return nestedAttr
                                })
                            }
                            return child
                        })
                    }
                    return parent
                })
            }
            
            this.allAttributes = [...this.allAttributes]
            this.$forceUpdate()
            
            // Force update selected attribute IDs and update filter state immediately
            this.$nextTick(() => {
                this.updateSelectedAttributeIds()
            })
        },
        
        removeSelectedCategory() {
            this.chooseCategory(null)
        },
        
        removeSelectedStore() {
            this.changeSelectedStore(null)
        },
        
        removeSelectedGovernorate() {
            this.changeSelectedGovernorate(null)
        },
        
        removeSelectedArea() {
            this.changeSelectedArea(null)
        },
        
        removeSelectedPrice() {
            this.filterObj.min_price = null
            this.filterObj.max_price = null
            this.price.min_price.val = ''
            this.price.max_price.val = ''
            this.updateFilterStateViaManager('removeSelectedPrice', {
                min_price: null,
                max_price: null,
            })
        },
        
        removeSelectedSpace() {
            this.filterObj.min_space = null
            this.filterObj.max_space = null
            this.spaces.min_space.val = ''
            this.spaces.max_space.val = ''
            this.updateFilterStateViaManager('removeSelectedSpace', {
                min_space: null,
                max_space: null,
            })
        },
        
        hasNestedAttributesSelected(child) {
            if (!child.attributes || !Array.isArray(child.attributes)) return false
            
            return child.attributes.some(nestedAttr => {
                if (!nestedAttr.children || !Array.isArray(nestedAttr.children)) return false
                return nestedAttr.children.some(nestedChild => nestedChild.isChecked)
            })
        },
        
        async initializeComponent() {
            try {
                await this.getCategories()
                await this.getGovernorates()
                await this.getStores()
                
                // Restore filter state from URL via manager
                // Manager handles URL parsing and state restoration
                this.filterManager.restoreFromUrl()
                
                // Wait for manager to restore state, then sync to filterObj for UI
                await this.$nextTick()
                
                // Get restored state from manager
                const restoredState = this.filterManager.getFilterState()
                
                // Sync manager state to filterObj for child components
                Object.keys(restoredState).forEach(key => {
                    if (key in this.filterObj && key !== 'filterQuery' && key !== 'attributesQuery') {
                        this.filterObj[key] = restoredState[key]
                    }
                })
                
                // Update userHasSelectedSorting if sort_by was restored
                if (restoredState.sort_by || restoredState.rating) {
                    this.userHasSelectedSorting = true
                }
                
                // Restore UI state from filterObj
                this.restoreUIStateFromFilterObj()
                
                const initialCategoryId = restoredState.category_id || restoredState.main_category_id
                
                if (initialCategoryId && initialCategoryId !== '0' && initialCategoryId !== 0) {
                    this.searchAllCategories = false
                    this.filterObj.category_id = initialCategoryId
                    this.mainCategoryId = restoredState.main_category_id || null
                    await this.getAttributes(initialCategoryId)
                    
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.$forceUpdate()
                        }, 250)
                    })
                } else {
                    this.searchAllCategories = true
                    this.filterObj.category_id = 0
                }
                
                // Mark initialization complete
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.isInitializing = false
                        this.filterManager.markInitialized()
                        this.$forceUpdate()
                    }, 100)
                })
            } catch (error) {
                console.error('Initialization error:', error)
                this.isInitializing = false
            }
        },
    },

    async mounted() {
        await this.initializeComponent()
        this.setupOverlayScrollHandler()
        // Query is already emitted in initializeComponent, no need to emit again
    },

    watch: {
        // Removed filterObj watcher - filter manager handles state updates and notifications
        // filterObj is now synced FROM manager, not TO manager via watcher
        '$route'(to, from) {
            // Only re-initialize if path changed or if this is initial navigation
            // Don't re-initialize on query param changes to prevent restoring removed filters
            if (!from || to.path !== from.path) {
                this.isInitializing = true
                this.initializeComponent().then(() => {
                    this.$forceUpdate()
                })
            }
        },
        decodeQueryObj: {
            handler(newVal, oldVal) {
                if (this.isInitializing) return
                
                const newMainCategoryId = newVal?.main_category_id
                const newCategoryId = newVal?.category_id
                const oldMainCategoryId = oldVal?.main_category_id
                const oldCategoryId = oldVal?.category_id
                
                if (newMainCategoryId !== oldMainCategoryId || newCategoryId !== oldCategoryId) {
                    this.isInitializing = true
                    this.initializeComponent().then(() => {
                        this.$forceUpdate()
                    })
                }
            },
            deep: true,
            immediate: false
        },
        'filterObj.category_id': {
            handler(newCategoryId) {
                if (newCategoryId && newCategoryId !== '0' && newCategoryId !== 0 && newCategoryId !== null) {
                    this.searchAllCategories = false
                } else {
                    this.searchAllCategories = true
                }
            },
            immediate: true
        }
    },

    beforeUnmount() {
        if (this.filterTimeout) clearTimeout(this.filterTimeout)
        if (this.yearChangeTimeout) clearTimeout(this.yearChangeTimeout)
        this.cleanupOverlayScrollHandler()
    },
}
</script>

<style scoped>
.scrollable {
    overflow: auto;
    scrollbar-width: none;
}

.scrollable::-webkit-scrollbar {
    display: none;
}

.transition-height-transform {
    transition-property: min-height, transform;
    transition-duration: 300ms;
    transition-timing-function: ease-in-out;
}

.bg-gray-25 {
    background-color: #fafafa;
}

.nested-attribute-item:hover {
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.transition-all {
    transition: all 0.2s ease-in-out;
}

.overlay-slide-enter-active,
.overlay-slide-leave-active {
    transition: all 0.2s ease-out;
}

.overlay-slide-enter-from {
    opacity: 0;
    transform: translateX(20px);
}

.overlay-slide-enter-to {
    opacity: 1;
    transform: translateX(0);
}

.overlay-slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.overlay-slide-leave-to {
    opacity: 0;
    transform: translateX(20px);
}

.nested-container {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.focus-ring:focus {
    outline: none;
    ring: 2px;
    ring-color: #3b82f6;
    ring-offset: 2px;
}

@media (min-width: 768px) {
    .filter-container {
        max-height: calc(100vh - 100px);
        overflow: hidden;
    }
    
    .filter-content {
        height: 100%;
        overflow-y: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
    }
    
    .filter-content::-webkit-scrollbar {
        display: none;
    }
}
</style>

